import { useEffect, useState, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, MapPin, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { buildEventPath } from "@/lib/event-url";

type CarouselEvent = {
  id: string;
  name: string;
  cover_image_url: string | null;
  start_date: string;
  location_name: string | null;
  location_address: string | null;
  sequential_id: number;
  user_id: string;
  producer_slug?: string;
};

const CAROUSEL_ASSET_VERSION = "20260410-refresh-1";

export function EventsCarousel() {
  const [events, setEvents] = useState<CarouselEvent[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchEvents = async () => {
      const { data: eventsData } = await supabase
        .from("public_events")
        .select("id, name, cover_image_url, start_date, location_name, location_address, sequential_id, user_id")
        .eq("status", "published")
        .gte("start_date", new Date().toISOString())
        .order("start_date", { ascending: true })
        .limit(50);

      if (!eventsData || eventsData.length === 0) {
        setLoading(false);
        return;
      }

      const userIds = [...new Set(eventsData.map((e) => e.user_id).filter(Boolean))];
      const { data: profiles } = await supabase
        .from("public_profiles")
        .select("user_id, slug")
        .in("user_id", userIds as string[]);

      const slugMap = new Map((profiles || []).map((p) => [p.user_id, p.slug]));

      const enriched = eventsData
        .filter((e): e is CarouselEvent & { id: string; sequential_id: number; user_id: string } =>
          !!e.id && !!e.sequential_id && !!e.user_id
        )
        .map((e) => ({
          ...e,
          id: e.id!,
          name: e.name || "",
          sequential_id: e.sequential_id!,
          user_id: e.user_id!,
          producer_slug: slugMap.get(e.user_id!) || undefined,
        }));

      setEvents(enriched);
      setActiveIndex(0);
      setLoading(false);
    };

    fetchEvents();
  }, []);

  const goTo = useCallback(
    (index: number) => {
      if (events.length === 0) return;
      setActiveIndex(((index % events.length) + events.length) % events.length);
    },
    [events.length]
  );

  const prev = () => goTo(activeIndex - 1);
  const next = () => goTo(activeIndex + 1);

  // Auto-advance
  useEffect(() => {
    if (events.length <= 1) return;
    const timer = setInterval(() => goTo(activeIndex + 1), 5000);
    return () => clearInterval(timer);
  }, [activeIndex, events.length, goTo]);

  // Touch/drag support
  const handlePointerDown = (e: React.PointerEvent) => {
    dragStartX.current = e.clientX;
    setIsDragging(false);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    const diff = e.clientX - dragStartX.current;
    if (Math.abs(diff) > 60) {
      setIsDragging(true);
      if (diff > 0) prev();
      else next();
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center h-80">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
          </div>
        </div>
      </section>
    );
  }

  if (events.length === 0) return null;

  const formatDate = (d: string) => {
    try {
      return new Date(d).toLocaleDateString("pt-BR", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return d;
    }
  };

  const getEventLink = (ev: CarouselEvent) => {
    if (ev.producer_slug) {
      return buildEventPath(ev.producer_slug, ev.name, ev.sequential_id);
    }
    return `/evento/${ev.sequential_id}`;
  };

  // Calculate visible range for a smooth sliding window
  const getCardStyle = (index: number): React.CSSProperties => {
    let diff = index - activeIndex;
    if (events.length > 2) {
      if (diff > events.length / 2) diff -= events.length;
      if (diff < -events.length / 2) diff += events.length;
    }

    const absDiff = Math.abs(diff);
    const isVisible = absDiff <= 2;

    if (!isVisible) {
      return {
        transform: `translateX(${diff > 0 ? 900 : -900}px) scale(0.7)`,
        opacity: 0,
        zIndex: 0,
        pointerEvents: "none",
        position: "absolute",
        transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      };
    }

    const translateX = diff * 320;
    const scale = absDiff === 0 ? 1 : absDiff === 1 ? 0.88 : 0.75;
    const opacity = absDiff === 0 ? 1 : absDiff === 1 ? 0.7 : 0.4;
    const zIndex = 10 - absDiff;

    return {
      transform: `translateX(${translateX}px) scale(${scale})`,
      opacity,
      zIndex,
      position: "absolute",
      transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      pointerEvents: absDiff === 0 ? "auto" : "auto",
      filter: absDiff > 0 ? `blur(${absDiff * 0.5}px)` : "none",
    };
  };

  return (
    <section className="py-16 bg-muted/20 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold">
            Próximos <span className="text-wine-gradient">eventos em destaque</span>
          </h2>
          <p className="text-muted-foreground mt-2">
            Agenda atualizada da Plateia.fun
          </p>
        </div>

        {/* Carousel */}
        <div
          ref={containerRef}
          className="relative flex items-center justify-center"
          style={{ minHeight: 460 }}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
        >
          {/* Left arrow */}
          <button
            onClick={prev}
            className="absolute left-2 md:left-8 z-30 w-10 h-10 rounded-full bg-card shadow-lg border border-border flex items-center justify-center hover:bg-muted transition-colors"
            aria-label="Anterior"
          >
            <ChevronLeft size={20} className="text-foreground" />
          </button>

          {/* Cards */}
          <div className="relative w-full max-w-6xl flex items-center justify-center" style={{ height: 520 }}>
            {events.map((ev, index) => {
              const diff = (() => {
                let d = index - activeIndex;
                if (events.length > 2) {
                  if (d > events.length / 2) d -= events.length;
                  if (d < -events.length / 2) d += events.length;
                }
                return d;
              })();
              const absDiff = Math.abs(diff);
              const isCenter = diff === 0;

              if (absDiff > 2) return null;

              return (
                <div
                  key={ev.id}
                  style={{
                    ...getCardStyle(index),
                    width: isCenter ? 520 : 400,
                  }}
                  className="cursor-pointer select-none"
                  onClick={() => !isCenter && goTo(index)}
                  aria-hidden={!isCenter}
                >
                  <Link
                    to={getEventLink(ev)}
                    className={`block ${!isCenter ? "pointer-events-none" : ""}`}
                    tabIndex={isCenter ? 0 : -1}
                    onClick={(e) => isDragging && e.preventDefault()}
                  >
                    <div
                      className={`rounded-2xl overflow-hidden bg-card transition-shadow duration-500 ${
                        isCenter
                          ? "shadow-[0_20px_60px_-15px_rgba(110,27,36,0.3)] ring-2 ring-primary/20"
                          : "shadow-lg"
                      }`}
                    >
                      {/* Image */}
                      <div className={`relative ${isCenter ? "h-80" : "h-56"} bg-muted overflow-hidden`}>
                        {ev.cover_image_url ? (
                          <img
                            src={`${ev.cover_image_url}${ev.cover_image_url.includes("?") ? "&" : "?"}v=${CAROUSEL_ASSET_VERSION}`}
                            alt={ev.name}
                            className="w-full h-full object-cover transition-transform duration-500"
                            style={{ transform: isCenter ? "scale(1.02)" : "scale(1)" }}
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full bg-wine-gradient flex items-center justify-center">
                            <span className="text-white font-heading text-lg font-bold text-center px-4">
                              {ev.name}
                            </span>
                          </div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/50 to-transparent" />
                      </div>

                      {/* Info */}
                      <div className="p-4">
                        <h3 className="font-heading font-bold text-sm md:text-base line-clamp-1 uppercase text-foreground">
                          {ev.name}
                        </h3>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                          {ev.location_name && (
                            <span className="flex items-center gap-1">
                              <MapPin size={12} className="text-primary" />
                              {ev.location_name}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar size={12} className="text-primary" />
                            {formatDate(ev.start_date)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>

          {/* Right arrow */}
          <button
            onClick={next}
            className="absolute right-2 md:right-8 z-30 w-10 h-10 rounded-full bg-card shadow-lg border border-border flex items-center justify-center hover:bg-muted transition-colors"
            aria-label="Próximo"
          >
            <ChevronRight size={20} className="text-foreground" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-0 mt-6">
          {events.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="relative flex items-center justify-center min-w-[24px] min-h-[24px] p-0"
              aria-label={`Ir para evento ${i + 1}`}
            >
              <span
                className={`rounded-full transition-all duration-500 block ${
                  i === activeIndex
                    ? "w-8 h-2.5 bg-primary"
                    : "w-2.5 h-2.5 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
